import { z } from 'zod';

export const userListDtoSchema = z.object({
  userId: z.string().nullable(),
  userName: z.string().nullable(),
  userEmail: z.string().nullable(),
  empNo: z.string().nullable(),
  practitionerTypeCode: z.enum(["doctor", "nurse"]).nullable(),
  practitionerTypeName: z.string().nullable(),
  userStatus: z.string().nullable(),
  lastLoginDatetime: z.string().nullable(),
  superAdminFlag: z.boolean().nullable(),
  userTenantLockVersion: z.number().nullable()
});
export type UserListDto = z.infer<typeof userListDtoSchema>;

export const apiDtoSchema = z.object({
  id: z.string().nullable(),
  apiName: z.string().nullable(),
  serviceCode: z.string().nullable(),
  apiUrl: z.string().nullable(),
  httpMethod: z.string().nullable()
});
export type ApiDto = z.infer<typeof apiDtoSchema>;

export const hospitalInfoResSchema = z.object({
  id: z.string().nullable(),
  contactPerson: z.string().nullable(),
  contactEmail: z.string().nullable(),
  nhiInstCode: z.string().nullable(),
  nhiInstName: z.string().nullable(),
  instAddress: z.string().nullable(),
  instTel: z.string().nullable(),
  instBoss: z.string().nullable(),
  instSuperintendent: z.string().nullable()
});
export type HospitalInfoRes = z.infer<typeof hospitalInfoResSchema>;

export const i18nResSchema = z.object({
  code: z.string().nullable(),
  description: z.string().nullable()
});
export type I18nRes = z.infer<typeof i18nResSchema>;

export const msgStatusEnumResSchema = z.object({
  code: z.number().nullable(),
  message: z.string().nullable(),
  statusCode: z.number().nullable()
});
export type MsgStatusEnumRes = z.infer<typeof msgStatusEnumResSchema>;

export const tableResSchema = z.object({
  schemaName: z.string().nullable(),
  tableName: z.string().nullable(),
  comment: z.string().nullable()
});
export type TableRes = z.infer<typeof tableResSchema>;

export const columnResSchema = z.object({
  columnName: z.string().nullable(),
  dataType: z.string().nullable(),
  comment: z.string().nullable(),
  nullable: z.boolean().nullable()
});
export type ColumnRes = z.infer<typeof columnResSchema>;

export const practitionerTypeResSchema = z.object({
  practitionerTypeCode: z.enum(["doctor", "nurse"]).nullable(),
  practitionerTypeName: z.string().nullable()
});
export type PractitionerTypeRes = z.infer<typeof practitionerTypeResSchema>;

export const userDetailResSchema = z.object({
  userId: z.string().nullable(),
  userEmail: z.string().nullable(),
  userName: z.string().nullable(),
  userMobile: z.string().nullable(),
  practitionerTypeCode: z.enum(["doctor", "nurse"]).nullable(),
  practitionerTypeName: z.string().nullable(),
  userStatus: z.string().nullable(),
  lastLoginDatetime: z.string().nullable(),
  superAdminFlag: z.boolean().nullable(),
  birthDate: z.string().nullable(),
  gender: z.string().nullable(),
  userTenantLockVersion: z.number().nullable(),
  userLockVersion: z.number().nullable(),
  genderI18n: z.string().nullable()
});
export type UserDetailRes = z.infer<typeof userDetailResSchema>;

export const queryUserListResSchema = z.object({
  userList: z.array(userListDtoSchema).nullable(),
  page: z.number().nullable(),
  size: z.number().nullable(),
  totalElements: z.number().nullable(),
  totalPages: z.number().nullable()
});
export type QueryUserListRes = z.infer<typeof queryUserListResSchema>;

export const userProfileResSchema = z.object({
  tenantName: z.string().nullable(),
  userName: z.string().nullable(),
  userEmail: z.string().nullable(),
  userMobile: z.string().nullable(),
  birthDate: z.string().nullable(),
  gender: z.enum(["male", "female", "other", "unknown"]).nullable(),
  practitionerTypeCode: z.enum(["doctor", "nurse"]).nullable(),
  practitionerTypeName: z.string().nullable(),
  dashboardCode: z.string().nullable(),
  dashboardName: z.string().nullable(),
  lockVersion: z.number().nullable()
});
export type UserProfileRes = z.infer<typeof userProfileResSchema>;

export const esignUploadResSchema = z.object({
  esignId: z.string().nullable()
});
export type EsignUploadRes = z.infer<typeof esignUploadResSchema>;

export const apiResponseListApiDtoSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(apiDtoSchema).nullable()
});
export type ApiResponseListApiDto = z.infer<typeof apiResponseListApiDtoSchema>;

export const apiResponseHospitalInfoResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: hospitalInfoResSchema.nullable()
});
export type ApiResponseHospitalInfoRes = z.infer<typeof apiResponseHospitalInfoResSchema>;

export const apiPageResponseObjectSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.unknown().nullable(),
  totalPageSize: z.number().nullable(),
  totalItemSize: z.number().nullable(),
  currentPageItemSize: z.number().nullable(),
  pageSize: z.number().nullable()
});
export type ApiPageResponseObject = z.infer<typeof apiPageResponseObjectSchema>;

export const apiResponseListI18nResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(i18nResSchema).nullable()
});
export type ApiResponseListI18nRes = z.infer<typeof apiResponseListI18nResSchema>;

export const apiResponseListMsgStatusEnumResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(msgStatusEnumResSchema).nullable()
});
export type ApiResponseListMsgStatusEnumRes = z.infer<typeof apiResponseListMsgStatusEnumResSchema>;

export const apiResponseListTableResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(tableResSchema).nullable()
});
export type ApiResponseListTableRes = z.infer<typeof apiResponseListTableResSchema>;

export const apiResponseListColumnResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(columnResSchema).nullable()
});
export type ApiResponseListColumnRes = z.infer<typeof apiResponseListColumnResSchema>;

export const apiResponseListPractitionerTypeResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(practitionerTypeResSchema).nullable()
});
export type ApiResponseListPractitionerTypeRes = z.infer<typeof apiResponseListPractitionerTypeResSchema>;

export const apiResponseUserDetailResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: userDetailResSchema.nullable()
});
export type ApiResponseUserDetailRes = z.infer<typeof apiResponseUserDetailResSchema>;

export const apiResponseQueryUserListResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: queryUserListResSchema.nullable()
});
export type ApiResponseQueryUserListRes = z.infer<typeof apiResponseQueryUserListResSchema>;

export const apiResponseUserProfileResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: userProfileResSchema.nullable()
});
export type ApiResponseUserProfileRes = z.infer<typeof apiResponseUserProfileResSchema>;

export const apiResponseObjectSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.unknown().nullable()
});
export type ApiResponseObject = z.infer<typeof apiResponseObjectSchema>;

export const reqCrudSaveSchema = z.object({
  systemNo: z.string().min(1),
  tableName: z.string().min(1),
  tableData: z.preprocess((val) => val === undefined ? null : val, z.array(z.unknown()).nullish())
});
export type ReqCrudSave = z.infer<typeof reqCrudSaveSchema>;

export const apiResponseEsignUploadResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: esignUploadResSchema.nullable()
});
export type ApiResponseEsignUploadRes = z.infer<typeof apiResponseEsignUploadResSchema>;

export const sendChangePasswordMailReqSchema = z.object({
  userId: z.string().min(1)
});
export type SendChangePasswordMailReq = z.infer<typeof sendChangePasswordMailReqSchema>;

export const activeUserReqSchema = z.object({
  userId: z.string().min(1),
  userTenantLockVersion: z.number()
});
export type ActiveUserReq = z.infer<typeof activeUserReqSchema>;

export const disableUserReqSchema = z.object({
  userId: z.string().min(1),
  disableReason: z.string().min(1),
  userTenantLockVersion: z.number()
});
export type DisableUserReq = z.infer<typeof disableUserReqSchema>;

export const removeUserReqSchema = z.object({
  userId: z.string().min(1),
  removeReason: z.string().min(1),
  userTenantLockVersion: z.number()
});
export type RemoveUserReq = z.infer<typeof removeUserReqSchema>;

export const resetPasswordReqSchema = z.object({
  newPassword: z.string().min(1),
  confirmPassword: z.string().min(1)
});
export type ResetPasswordReq = z.infer<typeof resetPasswordReqSchema>;

export const updatePasswordReqSchema = z.object({
  oldPassword: z.string().min(1),
  newPassword: z.string().min(1),
  confirmPassword: z.string().min(1)
});
export type UpdatePasswordReq = z.infer<typeof updatePasswordReqSchema>;

export const updateUserPractitionerTypeReqSchema = z.object({
  userId: z.string().min(1),
  practitionerTypeCode: z.enum(["doctor", "nurse"]),
  userLockVersion: z.number()
});
export type UpdateUserPractitionerTypeReq = z.infer<typeof updateUserPractitionerTypeReqSchema>;

export const apiResponseVoidSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.unknown().nullable()
});
export type ApiResponseVoid = z.infer<typeof apiResponseVoidSchema>;

export const updateUserProfileReqSchema = z.object({
  userName: z.string().min(1),
  userMobile: z.string().min(1),
  birthDate: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  gender: z.preprocess((val) => val === undefined ? null : val, z.enum(["MALE", "FEMALE", "OTHER", "UNKNOWN"]).nullish()),
  lockVersion: z.number()
});
export type UpdateUserProfileReq = z.infer<typeof updateUserProfileReqSchema>;

// Query Parameter Schemas

export const queryUserListQuerySchema = z.object({
  keyword: z.string().nullish(),
  userStatus: z.string().nullish(),
  practitionerTypeCodes: z.array(z.string()).nullish(),
  lastLoginDatetimeFrom: z.string().nullish(),
  lastLoginDatetimeTo: z.string().nullish(),
  sortField: z.string().nullish(),
  sortDirection: z.string().nullish(),
  page: z.number().nullish(),
  size: z.number().nullish()
});
export type QueryUserListQuery = z.infer<typeof queryUserListQuerySchema>;

export const queryQuerySchema = z.object({
  system: z.string(),
  tableName: z.string(),
  pageNum: z.number(),
  pageSize: z.number(),
  filters: z.string().nullish(),
  likes: z.string().nullish(),
  sorts: z.string().nullish()
});
export type QueryQuery = z.infer<typeof queryQuerySchema>;

export const queryStructureQuerySchema = z.object({
  system: z.string(),
  tableName: z.string()
});
export type QueryStructureQuery = z.infer<typeof queryStructureQuerySchema>;
